import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";

export default function AdminLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@rsinternational.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resettingSession, setResettingSession] = useState(true);
  const hasResetSession = useRef(false);

  useEffect(() => {
    if (loading || hasResetSession.current) return;
    hasResetSession.current = true;
    let cancelled = false;

    const resetSession = async () => {
      if (user && user !== false && user.role === "admin") {
        await logout();
      }
      if (!cancelled) setResettingSession(false);
    };

    resetSession();
    return () => {
      cancelled = true;
    };
  }, [loading, logout, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/admin/dashboard", { replace: true });
    else setError(res.error);
  };

  if (loading || resettingSession) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--rsi-purple)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="admin-login">
      <div className="absolute inset-0 bg-gradient-to-br from-[var(--rsi-purple)] via-[var(--rsi-navy)] to-[var(--rsi-purple)] grain" />
      <div
        className="absolute inset-0 opacity-30 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1638272181967-7d3772a91265?w=1920&q=85)",
        }}
      />

      <div className="relative min-h-screen flex items-center justify-center p-6">
        <Link
          to="/"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm"
          data-testid="login-back-home"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>

        <div className="w-full max-w-md glass rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm p-1">
              <img src={LOGO_URL} alt="" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">Admin Panel</div>
              <div className="font-display font-extrabold text-lg text-[var(--rsi-navy)]">
                R.S. INTERNATIONAL
              </div>
            </div>
          </div>

          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
            Sign in to your dashboard
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Secure access for trade desk administrators.
          </p>

          <form onSubmit={submit} className="mt-6 grid gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5"
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Sign In
            </button>
          </form>

          <div className="mt-6 text-xs text-gray-500 text-center">
            Protected by JWT auth · End-to-end encrypted session
          </div>
        </div>
      </div>
    </div>
  );
}
